import React, { useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import { AddRounded } from "@mui/icons-material";
import { showToast } from "services/utils/Status";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { RequestStatusChip } from "components/DesignSystem/RequestStatusChip";
import { detailCardSx } from "components/DesignSystem/RequestDetailField";
import { GlobalConfirmDialog } from "components/DesignSystem/sharedUiPatterns";
import { PrimaryButton, SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import { designColors, designType, designWeights } from "services/constants/designTokens";
import { Field } from "pages/PayRoll/PayInputs/Shared/formUi";
import { DateField, SelectField, TextField } from "pages/PayRoll/PayInputs/Shared/fieldInputs";
import { EmptyRow, Required, SimpleTable, tableCellSx } from "pages/PayRoll/Salary/Shared/salaryUi";
import { FormulaChip, PlainInput, SwitchLine } from "pages/PayRoll/Shared/statutoryUi";
import { COMPONENT_TABLE_HEADS } from "pages/PayRoll/Shared/statutoryConfigUi";
import {
  ACTIVE_STATUS_COLORS,
  PF_BASIS_OPTIONS,
  ROUNDING_OPTIONS,
  VERSION_STATUS_COLORS,
  activeVersion,
  displayFormula,
  draftWithSalaryCodes,
  formatDay,
  isLocked,
  salaryFormulaCodes,
  validateSettings,
} from "./pfConfigData";
import { createPfVersion, updatePfVersion } from "./pfConfigApi";
import { listSalaryComponents } from "pages/PayRoll/Salary/SalaryComponents/salaryComponentApi";
import { defaultAllSelection } from "services/constants";
import { PfComponentPanel } from "./PfComponentPanel";

// Configuration tab (P1): one PF version's settings and components. A
// version used by a payroll is locked; changes then go into a new version
// (Create New Version in the page header).

const clone = (value) => JSON.parse(JSON.stringify(value));

// White section card: title, one-line info, then the content.
const SettingsCard = ({ title, info, action, children, sx }) => (
  <Mui.Box sx={{ ...detailCardSx, p: { xs: 2, sm: 2.5 }, ...sx }}>
    <Mui.Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="space-between" sx={{ mb: 2 }}>
      <Mui.Box sx={{ minWidth: 0 }}>
        <Mui.Typography component="h2" sx={designType.sectionHeading}>{title}</Mui.Typography>
        {info ? <Mui.Typography sx={{ ...designType.helperText, mt: 0.25 }}>{info}</Mui.Typography> : null}
      </Mui.Box>
      {action}
    </Mui.Stack>
    {children}
  </Mui.Box>
);

const Grid = ({ columns, children, sx }) => (
  <Mui.Box sx={{ display: "grid", gridTemplateColumns: columns, columnGap: 2, rowGap: 2, ...sx }}>{children}</Mui.Box>
);

// No ⋮ menu: Status is changed in the component drawer.
const ComponentsTable = ({ title, info, kind, components, locked, onAdd, onOpen }) => {
  const rows = components.filter((component) => component.kind === kind);
  return (
    <SettingsCard
      title={`${title} (${rows.length})`}
      info={info}
      action={
        <SecondaryButton startIcon={<AddRounded />} onClick={onAdd} disabled={locked} sx={{ whiteSpace: "nowrap", flexShrink: 0 }}>
          Add Component
        </SecondaryButton>
      }
    >
      <SimpleTable fixed minWidth={820} heads={COMPONENT_TABLE_HEADS}>
        {rows.length ? (
          rows.map((row, index) => (
            <Mui.TableRow key={row.code} hover sx={{ opacity: row.status === "Active" ? 1 : 0.6 }}>
              <Mui.TableCell sx={tableCellSx}>{index + 1}</Mui.TableCell>
              <Mui.TableCell sx={{ ...tableCellSx, fontWeight: designWeights.semibold }}>{row.name}</Mui.TableCell>
              <Mui.TableCell sx={tableCellSx}>{row.code}</Mui.TableCell>
              <Mui.TableCell sx={tableCellSx}>
                <FormulaChip formula={displayFormula(row)} />
              </Mui.TableCell>
              <Mui.TableCell sx={{ ...tableCellSx, color: designColors.slate600 }}>{row.used_for || "–"}</Mui.TableCell>
              <Mui.TableCell sx={tableCellSx}>
                <RequestStatusChip status={row.status} colorCode={ACTIVE_STATUS_COLORS[row.status]} />
              </Mui.TableCell>
              <Mui.TableCell sx={tableCellSx}>
                <Mui.Stack direction="row" spacing={0.5} alignItems="center">
                  <SecondaryButton onClick={() => onOpen(row)} sx={{ minWidth: 0, minHeight: 32, px: 1.5 }}>
                    {locked || row.fixed ? "View" : "Edit"}
                  </SecondaryButton>
                </Mui.Stack>
              </Mui.TableCell>
            </Mui.TableRow>
          ))
        ) : (
          <EmptyRow colSpan={7} text="No components yet." />
        )}
      </SimpleTable>
    </SettingsCard>
  );
};

// Server errors for the components ("components", "components.3.formula").
const isComponentsKey = (key) => key === "components" || key.startsWith("components.");
const withoutComponentErrors = (serverErrors) =>
  Object.fromEntries(Object.entries(serverErrors).filter(([key]) => !isComponentsKey(key)));

export const PfSettingsTab = ({ domain, companyId, versions, setVersions, selectedId, setSelectedId, onSaved }) => {
  const selected = versions.find((version) => version.id === selectedId) || activeVersion(versions) || versions[0];
  const [form, setForm] = useState(() => clone(selected));
  const [showErrors, setShowErrors] = useState(false);
  const [serverErrors, setServerErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [panel, setPanel] = useState({ open: false, mode: "edit", kind: "CALCULATION", component: null });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [salaryCodes, setSalaryCodes] = useState([]);
  // loading | ready | error | no-company, shown above the formula editor.
  const [salaryCodesStatus, setSalaryCodesStatus] = useState({ state: "loading", message: "" });
  const [salaryCodesKey, setSalaryCodesKey] = useState(0);

  // The company's salary component codes for the formula editor
  // (GET payroll/salary/salary-component/?company_id=). Same company rule
  // as the Salary Components screen; "All companies" has no codes.
  useEffect(() => {
    if (!domain) return undefined;
    const id = Number(companyId);
    if (!Number.isInteger(id) || id <= 0 || id === defaultAllSelection) {
      setSalaryCodes([]);
      setSalaryCodesStatus({ state: "no-company", message: "" });
      return undefined;
    }
    const controller = new AbortController();
    setSalaryCodesStatus({ state: "loading", message: "" });
    listSalaryComponents(domain, companyId, controller.signal)
      .then((components) => {
        if (controller.signal.aborted) return;
        setSalaryCodes(salaryFormulaCodes(components));
        setSalaryCodesStatus({ state: "ready", message: "" });
      })
      .catch((loadError) => {
        if (controller.signal.aborted) return;
        setSalaryCodes([]);
        setSalaryCodesStatus({ state: "error", message: loadError?.message || "Could not load the salary component codes." });
      });
    return () => controller.abort();
  }, [domain, companyId, salaryCodesKey]);

  // Back to the saved values whenever another version is opened or saved.
  useEffect(() => {
    setForm(clone(selected));
    setShowErrors(false);
    setServerErrors({});
  }, [selected]);

  // A new version's formulas use the company's salary codes (BASIC_E ->
  // BASIC) once they are loaded.
  useEffect(() => {
    setForm((current) => draftWithSalaryCodes(current, salaryCodes));
  }, [selected, salaryCodes]);

  const locked = isLocked(form);
  const isDraft = form.status === "Draft";
  const dirty = JSON.stringify(form) !== JSON.stringify(selected);
  const errors = useMemo(() => validateSettings(form, versions), [form, versions]);
  const error = (key) => (showErrors ? errors[key] : undefined) || serverErrors[key];
  const componentsError = [
    showErrors ? errors.components : undefined,
    ...Object.entries(serverErrors).filter(([key]) => isComponentsKey(key)).map(([, message]) => message),
  ]
    .filter(Boolean)
    .join(" ");
  const active = activeVersion(versions);

  const set = (key) => (value) => {
    setForm((current) => ({ ...current, [key]: value }));
    setServerErrors((current) => (current[key] ? { ...current, [key]: undefined } : current));
  };
  const setComponents = (update) => {
    setForm((current) => ({ ...current, components: update(current.components) }));
    setServerErrors(withoutComponentErrors);
  };

  // Draft -> Save & Activate (POST); saved version -> Save (PUT). The server
  // supersedes the previous version; the parent reloads the list.
  const save = async () => {
    setShowErrors(true);
    if (Object.keys(errors).length) {
      showToast("error", errors.components || "Fix the highlighted fields before saving.");
      return;
    }
    setSaving(true);
    setServerErrors({});
    try {
      const { version, message } = isDraft
        ? await createPfVersion({ domain, companyId, form })
        : await updatePfVersion({ domain, companyId, form });
      showToast("success", message || (isDraft ? `${form.name} is active from ${formatDay(form.effective_from)}.` : `${form.name} saved.`));
      onSaved(version);
    } catch (err) {
      if (err.fieldErrors) setServerErrors(err.fieldErrors);
      if (err.code === "STALE_VERSION") {
        showToast("error", "Someone else changed this version. Reloading…");
        onSaved({ id: form.id });
        return;
      }
      if (err.code === "VERSION_LOCKED") {
        showToast("error", err.message);
        onSaved({ id: form.id });
        return;
      }
      showToast("error", err.message);
    } finally {
      setSaving(false);
    }
  };

  // The draft never reached the server: just drop it.
  const discardDraft = () => {
    setVersions((list) => list.filter((version) => version.id !== form.id));
    setSelectedId(active?.id ?? null);
    setDiscardOpen(false);
    showToast("success", "Draft discarded.");
  };

  const saveComponent = (component) => {
    setComponents((list) =>
      panel.mode === "add" ? [...list, component] : list.map((item) => (item.code === panel.component.code ? component : item)),
    );
    setPanel((current) => ({ ...current, open: false }));
  };

  return (
    <Mui.Stack spacing={2}>
      {locked ? (
        <RequestInfoBanner
          tone="warning"
          title={`${form.name} is locked.`}
          body={`Used by payroll ${(form.used_by || []).join(", ") || "runs"}, so past months never change. Create a new version to change PF settings.`}
          action={
            form.id !== active?.id ? (
              <SecondaryButton onClick={() => setSelectedId(active.id)} sx={{ whiteSpace: "nowrap" }}>Open Active Version</SecondaryButton>
            ) : null
          }
        />
      ) : isDraft && active ? (
        <RequestInfoBanner
          tone="purple"
          title={`Draft — copied from ${active.name}.`}
          body={`Change what you need and pick the month it starts. Saving makes it the active version from that month; ${active.name} ends the day before.`}
        />
      ) : isDraft ? (
        <RequestInfoBanner
          tone="purple"
          title="First PF version."
          body="Enter the wage ceiling, rates and due day, and pick the month it starts. Saving makes it the active version from that month."
        />
      ) : (
        <RequestInfoBanner
          title={`Applies from ${formatDay(form.effective_from)} for payrolls not yet finalised.`}
          body="Once a payroll uses this version it is locked. To change it after that, create a new version from a new month."
        />
      )}

      <SettingsCard title="Version Details">
        <Grid columns={{ xs: "1fr", md: "2fr 2fr 1fr" }}>
          <Field label={<>Version Name<Required /></>} error={error("name")}>
            <TextField value={form.name} onChange={set("name")} disabled={locked} error={error("name")} />
          </Field>
          <Field label={<>Effective From<Required /> (1st of a month)</>} error={error("effective_from")}>
            <DateField value={form.effective_from} onChange={set("effective_from")} disabled={locked || !isDraft} error={error("effective_from")} />
          </Field>
          <Field label="Status">
            <Mui.Box sx={{ minHeight: 40, display: "flex", alignItems: "center" }}>
              <RequestStatusChip status={form.status} colorCode={VERSION_STATUS_COLORS[form.status]} />
            </Mui.Box>
          </Field>
          <Mui.Box sx={{ gridColumn: "1 / -1" }}>
            <Field label="Description">
              <TextField value={form.description} onChange={set("description")} disabled={locked} placeholder="What changed and why" />
            </Field>
          </Mui.Box>
        </Grid>
      </SettingsCard>

      <Grid columns={{ xs: "1fr", lg: "1fr 1fr" }}>
        <SettingsCard title="Wage Ceiling & Basis" info="Used by the PF wage components">
          <Grid columns={{ xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" }}>
            <Field label={<>PF wage ceiling (₹ / month)<Required /></>} error={error("wage_ceiling")}>
              <PlainInput type="number" value={form.wage_ceiling} onChange={set("wage_ceiling")} disabled={locked} error={error("wage_ceiling")} />
            </Field>
            <Field label={<>EPS wage cap (₹)<Required /></>} error={error("eps_cap")}>
              <PlainInput type="number" value={form.eps_cap} onChange={set("eps_cap")} disabled={locked} error={error("eps_cap")} />
            </Field>
            <Field label={<>Stop EPS at age<Required /></>} error={error("eps_stop_age")}>
              <PlainInput type="number" value={form.eps_stop_age} onChange={set("eps_stop_age")} disabled={locked} error={error("eps_stop_age")} />
            </Field>
          </Grid>
          <Grid columns={{ xs: "1fr", sm: "3fr 2fr" }} sx={{ mt: 2, alignItems: "end" }}>
            <Field label={<>PF basis — employee and employer (default)<Required /></>} error={error("pf_basis_default")}>
              <SelectField value={form.pf_basis_default} onChange={(value) => value && set("pf_basis_default")(value)} options={PF_BASIS_OPTIONS} disabled={locked} />
            </Field>
            <Mui.Typography sx={{ ...designType.captionText, pb: 1.25 }}>One basis for both shares, so the ECR has one EPF wage.</Mui.Typography>
          </Grid>
          <Mui.Box sx={{ mt: 1.5 }}>
            <RequestInfoBanner body="EPS needs every PF member's date of birth (People record). Missing DOB is a blocking validation issue." />
          </Mui.Box>
        </SettingsCard>

        <SettingsCard title="Employee Options" info="What HR may set per employee (Statutory › PF)">
          <SwitchLine
            checked={form.vpf_on_actual_allowed}
            onChange={set("vpf_on_actual_allowed")}
            disabled={locked}
            label="Allow contribution on actual wage (employee and employer)"
          />
          <SwitchLine checked={form.vpf_enabled} onChange={set("vpf_enabled")} disabled={locked} label="Allow Voluntary PF (VPF) — max">
            <PlainInput
              type="number"
              value={form.vpf_max_pct}
              onChange={set("vpf_max_pct")}
              disabled={locked || !form.vpf_enabled}
              error={error("vpf_max_pct")}
              inputProps={{ min: 1, max: 100, "aria-label": "VPF maximum percent" }}
              sx={{ width: 84 }}
            />
            <Mui.Typography sx={designType.tableBodyCell}>% of PF wage</Mui.Typography>
          </SwitchLine>
          {error("vpf_max_pct") ? (
            <Mui.Typography sx={{ ...designType.captionText, color: "#DC2626", ml: 7 }}>{error("vpf_max_pct")}</Mui.Typography>
          ) : null}
          <SwitchLine
            checked={form.opt_out_allowed}
            onChange={set("opt_out_allowed")}
            disabled={locked}
            label="Allow opt-out for new joiners above the ceiling"
          />
          <Grid columns={{ xs: "1fr", sm: "1fr 1fr" }} sx={{ mt: 1.5 }}>
            <Field label={<>Challan due day — day of next month<Required /></>} error={error("challan_due_day")}>
              <PlainInput type="number" value={form.challan_due_day} onChange={set("challan_due_day")} disabled={locked} error={error("challan_due_day")} inputProps={{ min: 1, max: 28 }} />
            </Field>
            <Field label="Rounding">
              <SelectField value={form.rounding} onChange={(value) => value && set("rounding")(value)} options={ROUNDING_OPTIONS} disabled={locked} />
            </Field>
          </Grid>
        </SettingsCard>
      </Grid>

      <ComponentsTable
        title="Calculation Components"
        info="Wage bases PF uses"
        kind="CALCULATION"
        components={form.components}
        locked={locked}
        onAdd={() => setPanel({ open: true, mode: "add", kind: "CALCULATION", component: null })}
        onOpen={(component) => setPanel({ open: true, mode: "edit", kind: component.kind, component })}
        onToggle={(component) =>
          setComponents((list) => list.map((item) => (item.code === component.code ? { ...item, status: item.status === "Active" ? "Inactive" : "Active" } : item)))
        }
        onDelete={setDeleteTarget}
      />
      <ComponentsTable
        title="Contribution Components"
        info="Employee deductions and employer costs"
        kind="CONTRIBUTION"
        components={form.components}
        locked={locked}
        onAdd={() => setPanel({ open: true, mode: "add", kind: "CONTRIBUTION", component: null })}
        onOpen={(component) => setPanel({ open: true, mode: "edit", kind: component.kind, component })}
        onToggle={(component) =>
          setComponents((list) => list.map((item) => (item.code === component.code ? { ...item, status: item.status === "Active" ? "Inactive" : "Active" } : item)))
        }
        onDelete={setDeleteTarget}
      />
      {componentsError ? <RequestInfoBanner tone="danger" body={componentsError} /> : null}

      {!locked ? (
        <Mui.Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={1.5} justifyContent="flex-end" alignItems={{ sm: "center" }}>
          {isDraft ? (
            <Mui.Button onClick={() => setDiscardOpen(true)} sx={{ color: "#DC2626", textTransform: "none", fontWeight: designWeights.semibold, mr: { sm: "auto" } }}>
              Discard Draft
            </Mui.Button>
          ) : null}
          <SecondaryButton onClick={() => setForm(draftWithSalaryCodes(clone(selected), salaryCodes))} disabled={!dirty}>
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={save} loading={saving} disabled={saving || (!dirty && !isDraft)}>
            {isDraft ? "Save & Activate" : "Save"}
          </PrimaryButton>
        </Mui.Stack>
      ) : null}

      <PfComponentPanel
        open={panel.open}
        mode={panel.mode}
        kind={panel.kind}
        component={panel.component}
        form={form}
        salaryCodes={salaryCodes}
        salaryCodesStatus={salaryCodesStatus}
        onRetrySalaryCodes={() => setSalaryCodesKey((key) => key + 1)}
        readOnly={locked}
        onClose={() => setPanel((current) => ({ ...current, open: false }))}
        onSave={saveComponent}
      />

      <GlobalConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete component"
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => {
          setComponents((list) => list.filter((item) => item.code !== deleteTarget.code));
          setDeleteTarget(null);
        }}
        confirmLabel="Delete"
        cancelLabel="Keep"
        maxWidth="sm"
        danger
      >
        Delete {deleteTarget?.name} ({deleteTarget?.code}) from {form.name}? Formulas that use it will show an error.
      </GlobalConfirmDialog>

      <GlobalConfirmDialog
        open={discardOpen}
        title="Discard draft"
        onCancel={() => setDiscardOpen(false)}
        onConfirm={discardDraft}
        confirmLabel="Discard"
        cancelLabel="Keep Draft"
        maxWidth="sm"
        danger
      >
        Discard {form.name}? {active ? `${active.name} stays the active version.` : "PF stays not set up."}
      </GlobalConfirmDialog>
    </Mui.Stack>
  );
};
